// 文件名 / YAML 解析，约定与 reellax-go/cmd/import 一致。

export interface ParsedSubtitleName {
  season: number
  episode: number
  locale: string
}

/** S02E01.en.srt */
const SEASON_NAME_RE = /^[sS](\d+)[eE](\d+)\.([a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)?)$/
/** 1.en.srt（默认季） */
const SHORT_NAME_RE = /^(\d+)\.([a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)?)$/

export function parseSubtitleName(fileName: string, defaultSeason = 1): ParsedSubtitleName | null {
  const stem = fileName.replace(/\.[^.]+$/, '')
  const seasonMatch = SEASON_NAME_RE.exec(stem)
  if (seasonMatch) {
    const season = Number(seasonMatch[1])
    const episode = Number(seasonMatch[2])
    if (!Number.isInteger(season) || season < 1 || !Number.isInteger(episode) || episode < 1) return null
    return { season, episode, locale: seasonMatch[3].toLowerCase() }
  }
  const short = SHORT_NAME_RE.exec(stem)
  if (short) {
    const episode = Number(short[1])
    if (!Number.isInteger(episode) || episode < 1) return null
    if (!Number.isInteger(defaultSeason) || defaultSeason < 1) return null
    return { season: defaultSeason, episode, locale: short[2].toLowerCase() }
  }
  return null
}

export function defaultSubtitleName(season: number, episode: number, locale: string): string {
  return `S${String(season).padStart(2, '0')}E${String(episode).padStart(2, '0')}.${locale}.srt`
}

/** 读文件为文本：去 BOM，UTF-8 坏掉时再试 GB18030。 */
export function readTextFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error(`读取失败：${file.name}`))
    reader.onload = () => {
      resolve(decodeTextBytes(reader.result))
    }
    reader.readAsArrayBuffer(file)
  })
}

function decodeTextBytes(raw: string | ArrayBuffer | null): string {
  if (raw == null) return ''
  if (typeof raw === 'string') return stripBom(raw)
  const bytes = new Uint8Array(raw)
  let offset = 0
  let utfLabel: 'utf-8' | 'utf-16le' | 'utf-16be' = 'utf-8'
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    offset = 3
  } else if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    utfLabel = 'utf-16le'
    offset = 2
  } else if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    utfLabel = 'utf-16be'
    offset = 2
  }
  const slice = bytes.subarray(offset)
  const utf = new TextDecoder(utfLabel).decode(slice)
  if (utfLabel !== 'utf-8') return utf
  const badUtf = replacementCount(utf)
  if (badUtf === 0) return utf
  try {
    const gbk = new TextDecoder('gb18030').decode(slice)
    if (replacementCount(gbk) < badUtf) return gbk
  } catch {
    // 无 gb18030 时沿用 UTF-8
  }
  return utf
}

function replacementCount(text: string): number {
  return text.match(/\uFFFD/g)?.length ?? 0
}

function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
}

export interface YamlLocale {
  locale: string
  title: string
  description: string
}

export interface ParsedShowYaml {
  id: number
  locales: YamlLocale[]
  genres?: string[]
  tropes?: string[]
}

/**
 * 与 cmd/import 同结构：id / 可选 genres·tropes / locales / 4 空格语言键 / 6 空格 title|description。
 */
export function parseShowYaml(content: string): ParsedShowYaml {
  const locales = new Map<string, { title: string; description: string }>()
  const genres: string[] = []
  const tropes: string[] = []
  let id = 0
  let currentLocale = ''
  let inDesc = false
  let descLines: string[] = []
  let listKind: 'genres' | 'tropes' | '' = ''
  let sawGenres = false
  let sawTropes = false

  const flushDesc = () => {
    if (!inDesc || !currentLocale) return
    const prev = locales.get(currentLocale) ?? { title: '', description: '' }
    prev.description = descLines.join('\n').trim()
    locales.set(currentLocale, prev)
    inDesc = false
    descLines = []
  }

  const extractValue = (raw: string) => {
    let s = raw.trim()
    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
      s = s.slice(1, -1)
    }
    return s
  }

  for (const raw of content.split(/\r?\n/)) {
    const line = raw.replace(/\s+$/, '')
    if (inDesc) {
      const indent = line.length - line.replace(/^ +/, '').length
      if (line.trim() === '' || indent >= 7) {
        descLines.push(line.trim())
        continue
      }
      flushDesc()
    }

    const trimmed = line
    if (trimmed.startsWith('- id:')) {
      listKind = ''
      const n = Number(trimmed.slice(5).trim())
      if (!Number.isInteger(n) || n <= 0) throw new Error('YAML 中 id 无效')
      id = n
      continue
    }
    if (/^\s*id:\s*/.test(trimmed) && !trimmed.trimStart().startsWith('-')) {
      const n = Number(trimmed.replace(/^\s*id:\s*/, '').trim())
      if (Number.isInteger(n) && n > 0) id = n
      continue
    }
    const genreKey = /^\s*genres:\s*(.*)$/.exec(trimmed)
    if (genreKey) {
      listKind = 'genres'
      sawGenres = true
      currentLocale = ''
      for (const v of parseYamlSlugList(genreKey[1], extractValue)) genres.push(v)
      continue
    }
    const tropeKey = /^\s*tropes:\s*(.*)$/.exec(trimmed)
    if (tropeKey) {
      listKind = 'tropes'
      sawTropes = true
      currentLocale = ''
      for (const v of parseYamlSlugList(tropeKey[1], extractValue)) tropes.push(v)
      continue
    }
    if (trimmed.trim() === 'locales:' || trimmed.trimStart().startsWith('locales:')) {
      listKind = ''
      continue
    }
    if (listKind && /^\s*-\s+/.test(trimmed)) {
      const val = extractValue(trimmed.replace(/^\s*-\s+/, ''))
      if (!val) continue
      if (listKind === 'genres') genres.push(val)
      else tropes.push(val)
      continue
    }

    // 语言键：4 空格缩进（排除列表项）
    if (/^ {4}[^\s-].*:$/.test(trimmed) || /^ {4}[^:\s-]+:\s*$/.test(trimmed)) {
      listKind = ''
      currentLocale = trimmed.trim().replace(/:$/, '').replace(/^["']|["']$/g, '')
      if (!locales.has(currentLocale)) locales.set(currentLocale, { title: '', description: '' })
      continue
    }

    if (currentLocale && /^\s{6}title:/.test(trimmed)) {
      listKind = ''
      const prev = locales.get(currentLocale) ?? { title: '', description: '' }
      prev.title = extractValue(trimmed.replace(/^\s*title:\s*/, ''))
      locales.set(currentLocale, prev)
      continue
    }

    if (currentLocale && /^\s{6}description:/.test(trimmed)) {
      listKind = ''
      const val = trimmed.replace(/^\s*description:\s*/, '').trim()
      if (val === '|' || val === '|-' || val === '|+') {
        inDesc = true
        descLines = []
        continue
      }
      const prev = locales.get(currentLocale) ?? { title: '', description: '' }
      prev.description = extractValue(val)
      locales.set(currentLocale, prev)
    }
  }
  flushDesc()

  if (id <= 0) throw new Error('YAML 未找到 id')
  const list: YamlLocale[] = []
  for (const [locale, info] of locales) {
    if (!info.title.trim()) continue
    list.push({ locale, title: info.title.trim(), description: info.description.trim() })
  }
  if (list.length === 0) throw new Error('YAML 中没有任何有效语言标题')
  const out: ParsedShowYaml = { id, locales: list }
  if (sawGenres) out.genres = genres
  if (sawTropes) out.tropes = tropes
  return out
}

function parseYamlSlugList(rest: string, extractValue: (s: string) => string): string[] {
  const s = rest.trim()
  if (!s || s === '[]') return []
  if (s.startsWith('[') && s.endsWith(']')) {
    const inner = s.slice(1, -1).trim()
    if (!inner) return []
    return inner
      .split(',')
      .map((p) => extractValue(p))
      .filter(Boolean)
  }
  const one = extractValue(s)
  return one ? [one] : []
}
